import React, { useCallback, useContext, useEffect, useState } from 'react';
import Credentials from './Credentials';
import PersonalDetails from './PersonalDetails';
import Phone from './Phone';
import MiscUtils from 'services/MiscUtils';
import './style.scss';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { useAppSelector } from 'careand-redux/store';
import { api } from 'careand-redux/api/ApiThunks';
import { usePathParams } from 'components/Navigation/NavStack';
import { useNavStack } from 'careand-redux/slices/ui/hooks';
import { Stacks } from 'careand-redux/slices/ui/navigation';
import TopBar from 'scenes/HomePatient2/TopBar';
import Confirmation from 'scenes/PatientRegistration/Confirmation';
import { registrationActions } from 'careand-redux/slices/registration';
import Welcome from 'scenes/PatientRegistration/Welcome';
import EntryView from 'scenes/HomePatient2/PatientEntry/view';
import { REGISTRATION_STEPS } from 'scenes/PatientRegistration/RegistrationUtils';
import AnalyticsContext from 'contexts/AnalyticsContext';
import { getTenantIdentity } from 'hooks/useTenantIdentity';
import TenantImg from '../../components/TenantImg';

const PatientRegistration = () => {
  const { trackTagManager } = useContext(AnalyticsContext);
  const params = usePathParams();
  const dispatch = useAppDispatch();
  const nav = useNavStack(Stacks.Master);
  const track_id = useAppSelector((state) => state.registration.track_id);
  const currentStep = params.step || REGISTRATION_STEPS.credentials.route;
  const [isLinking, setLinking] = useState(false);
  const isLoggedIn = useAppSelector((state) => state.authentication.loggedIn);

  useEffect(() => {
    setLinking(isLoggedIn);
  }, []);

  const track = useCallback(
    (screen, payload = {}) => {
      if (track_id) {
        payload.track_id = track_id;
      }
      MiscUtils.getBrowserFingerprint((device_id) => {
        let userTracking = {
          device_id,
          screen,
          payload: payload ? JSON.stringify(payload) : undefined,
        };
        dispatch(api.Resource.trackUser({ userTracking }));
      });
    },
    [track_id, dispatch],
  );

  function onFinishFlow(linking = false) {
    if (linking) {
      nav.popTo('/', { animation: 'push' });
    } else {
      trackTagManager('register');
      nav.popTo('/welcome', { animation: 'push' });
    }
    dispatch(registrationActions.registerPatientCompleted());
  }

  function finishedStep() {
    let result;
    let step = currentStep;
    if (step === REGISTRATION_STEPS.credentials.route) {
      result = REGISTRATION_STEPS.personal.route;
    } else if (step === REGISTRATION_STEPS.personal.route) {
      result = REGISTRATION_STEPS.phone.route;
    } else if (step === REGISTRATION_STEPS.phone.route) {
      result = REGISTRATION_STEPS.verification.route;
    } else if (step === REGISTRATION_STEPS.verification.route) {
      result = REGISTRATION_STEPS.welcome.route;
      if (isLinking) {
        onFinishFlow(true);
        return;
      }
    }
    nav.push(`/register/${result}`);
  }

  function goToRoot() {
    nav.popToRoot();
  }

  const topLabel = REGISTRATION_STEPS[currentStep]?.label || '';

  const isWelcome = currentStep === REGISTRATION_STEPS.welcome.route;

  return (
    <EntryView
      className="patient-registration"
      topContent={!isWelcome ? <TopBar title={topLabel} /> : null}
      pageContentClassName={currentStep}
      key={currentStep}
      hideImage={isWelcome}
      limitedWidth={false}
    >
      {!isWelcome && (
        <div className="desktop-only">
          <div className="top-content">
            <TenantImg src="logo_full_color" alt={getTenantIdentity().name} className="logo" />
            {topLabel && <span className="top-text large desktop-only">{topLabel}</span>}
          </div>
        </div>
      )}
      {currentStep === REGISTRATION_STEPS.credentials.route && (
        <Credentials onSuccess={finishedStep} track={track} />
      )}
      {currentStep === REGISTRATION_STEPS.personal.route && (
        <PersonalDetails onSuccess={finishedStep} goToStart={goToRoot} track={track} />
      )}
      {currentStep === REGISTRATION_STEPS.phone.route && (
        <Phone onSuccess={finishedStep} goToStart={goToRoot} track={track} />
      )}
      {currentStep === REGISTRATION_STEPS.verification.route && (
        <Confirmation
          onSuccess={finishedStep}
          onBack={() => nav.pop()}
          goToStart={goToRoot}
          track={track}
        />
      )}
      {currentStep === REGISTRATION_STEPS.welcome.route && (
        <Welcome onSuccess={() => onFinishFlow(false)} track={track} />
      )}
    </EntryView>
  );
};

export default PatientRegistration;
